import { lastminuteDealsGradientOnButton } from '@/app/common/components/Promotions/styled';
import { PriceSkeletons, PriceSummary } from '@/app/components/hotel-detail/PriceSummary';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { Button, type ButtonSize } from '@lmn/design-system';
import styled, { css } from 'styled-components';

// The HTMLAttributes half is load-bearing: off Evo, ChangeTransportDetailsModal styles this panel
// through a styled-components `css` prop, which the SWC transform turns into a wrapper passing
// `className` — so `...rest` below must keep forwarding it to the flex row.
type BottomContinuePanelProps = {
  isLmnDeal?: boolean;
  selectedMealPlan: MealPlanType;
  showPrice: boolean;
  showStrikeThroughPrice?: boolean;
  buttonSize?: ButtonSize;
  wideButton?: boolean;
  onContinueClick: () => void;
} & React.HTMLAttributes<HTMLDivElement>;

const StyledButton = styled(Button)<{ $isLmnDeal?: boolean; $wide?: boolean }>`
  ${(p) => p.$isLmnDeal && lastminuteDealsGradientOnButton};
  /* The design-system sizes only change vertical padding and keep 16px horizontal — which is what the mobile
     bottom sheet wants (Figma 979-40680). The desktop footer CTA uses 40px (space500) instead, so widen it
     there only: the two are independent, hence a separate prop rather than keying off the size.
     The doubled selector raises specificity because these longhands have to beat the design system's
     padding shorthand, which is otherwise only a matter of stylesheet injection order. */
  ${(p) =>
    p.$wide &&
    css`
      && {
        padding-left: ${p.theme.kallisto.space.space500};
        padding-right: ${p.theme.kallisto.space.space500};
      }
    `}
`;

export function BottomContinuePanel(props: BottomContinuePanelProps) {
  const {
    selectedMealPlan,
    onContinueClick,
    showPrice,
    isLmnDeal = false,
    showStrikeThroughPrice = false,
    buttonSize = 'regular',
    wideButton = false,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();

  return (
    <div
      css={`
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
      `}
      {...rest}
    >
      {showPrice ? (
        <PriceSummary
          mealPlan={selectedMealPlan}
          showLmnDeal={isLmnDeal}
          showStrikeThroughPrice={showStrikeThroughPrice}
        />
      ) : (
        <PriceSkeletons />
      )}
      <StyledButton
        variant={'primary'}
        size={buttonSize}
        onClick={onContinueClick}
        disabled={!showPrice}
        $isLmnDeal={isLmnDeal}
        $wide={wideButton}
      >
        {accommodationOnly ? t('vacation.select.details.book.b') : t('vacation.select.details.book')}
      </StyledButton>
    </div>
  );
}
